import type { PageJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';

export const applyColorsToMonogram = (json: PageJson, background?: string, fontColor?: string): PageJson => {
  return {
    fill: background || json.fill,
    layers: json.layers.map(layer => {
      if (layer.type === 'text') {
        return {
          ...layer,
          textData: {
            ...layer.textData,
            fill: fontColor || layer.textData.fill,
          },
        };
      }

      return layer;
    }),
  };
};

// Replaces date ("MM.DD") and year ("YYYY") text layers with values from the event date.
const DATE_RE = /^\d{1,2}\.\d{1,2}$/;
const YEAR_RE = /^\d{4}$/;
export const applyEventDateToMonogram = (json: PageJson, eventDate: number): PageJson => {
  const date = new Date(eventDate);
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const monthDay = `${month}.${day}`;
  const year = String(date.getUTCFullYear());
  return {
    ...json,
    layers: json.layers.map(layer => {
      if (layer.type !== 'text' || !layer.editable) return layer;
      if (DATE_RE.test(layer.textData.content)) return { ...layer, textData: { ...layer.textData, content: monthDay } };
      if (YEAR_RE.test(layer.textData.content)) return { ...layer, textData: { ...layer.textData, content: year } };
      return layer;
    }),
  };
};

// Assigns initials by (y, x) reading order, not JSON document order — templates author letter layers in arbitrary order.
const SINGLE_LETTER_RE = /^[a-zA-Z]$/;
const isSingleLetterLayer = (layer: PageJson['layers'][number]) => layer.type === 'text' && layer.editable && SINGLE_LETTER_RE.test(layer.textData.content);

export const applyInitialsToMonogram = (json: PageJson, initials: ReadonlyArray<string>): PageJson => {
  const initialByLayerIndex = new Map<number, string>();
  json.layers
    .map((layer, index) => ({ layer, index }))
    .filter(entry => isSingleLetterLayer(entry.layer))
    .sort((a, b) => a.layer.layout.y - b.layer.layout.y || a.layer.layout.x - b.layer.layout.x)
    .forEach((entry, i) => {
      const initial = initials[i];
      if (initial) initialByLayerIndex.set(entry.index, initial);
    });

  return {
    ...json,
    layers: json.layers.map((layer, index) => {
      const initial = initialByLayerIndex.get(index);
      if (!initial || layer.type !== 'text') return layer;
      const content = layer.textData.content === layer.textData.content.toLowerCase() ? initial.toLowerCase() : initial.toUpperCase();
      return { ...layer, textData: { ...layer.textData, content } };
    }),
  };
};

/** Returns the uppercase first letter of each partner's first name, or empty if either is missing.
 * Partial substitution (one real initial + one template placeholder) produces mixed user-visible content. */
export const deriveMonogramInitials = (ownerFirstName: string | null | undefined, fianceeFirstName: string | null | undefined): ReadonlyArray<string> => {
  const initials = [ownerFirstName, fianceeFirstName].map(name => name?.trim()[0]?.toUpperCase() ?? '').filter(Boolean);
  return initials.length === 2 ? initials : [];
};

/**
 * Applies initials and event date to a raw monogram JSON container content object.
 * Returns the original content unchanged when the required data is absent.
 */
export const personalizeMonogramContent = (
  content: Record<string, unknown>,
  ownerFirstName: string | null | undefined,
  fianceeFirstName: string | null | undefined,
  eventDateMs: number | null | undefined,
): Record<string, unknown> => {
  const card = content?.card as Record<string, unknown> | undefined;
  const front = card?.front as PageJson | undefined;
  if (!front) return content;

  let personalizedFront = front;
  const initials = deriveMonogramInitials(ownerFirstName, fianceeFirstName);
  if (initials.length) {
    personalizedFront = applyInitialsToMonogram(personalizedFront, initials);
  }
  if (eventDateMs) {
    personalizedFront = applyEventDateToMonogram(personalizedFront, eventDateMs);
  }
  return { ...content, card: { ...card, front: personalizedFront } };
};
